'use client';

import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { SegmentedControl } from '../../ui';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { CatalogSortBar } from './CatalogSortBar';
import { ALL_CATEGORIES, sortCatalogCases } from './catalogSort';
import { categoryName, type CaseCatalog as CaseCatalogData, type CatalogCase, type CatalogSort } from './catalog.types';
import translations from './CaseCatalog.translations';

interface CaseCatalogProps {
  catalog: CaseCatalogData;
  sort: CatalogSort;
  onSortChange: (sort: CatalogSort) => void;
  category: string;
  onCategoryChange: (slug: string) => void;
  gridClassName: string;
  renderCase: (caseItem: CatalogCase) => ReactNode;
}

export const CaseCatalog: FC<CaseCatalogProps> = ({ catalog, sort, onSortChange, category, onCategoryChange, gridClassName, renderCase }) => {
  const { t, lang } = useLocalTranslation(translations);
  const categories = useMemo(() => catalog.categories.filter((c) => c.cases.length > 0), [catalog.categories]);

  const active = categories.find((c) => c.slug === category);
  const activeSlug = active?.slug ?? ALL_CATEGORIES;

  const segments = useMemo(
    () => [{ id: ALL_CATEGORIES, label: t('allTab') }, ...categories.map((c) => ({ id: c.slug, label: categoryName(c, lang) }))],
    [categories, t, lang],
  );

  const cases = useMemo(
    () => sortCatalogCases(active ? active.cases : catalog.general, sort, lang),
    [active, catalog.general, sort, lang],
  );

  return (
    <div className="flex flex-col">
      {categories.length > 0 && <SegmentedControl segments={segments} value={activeSlug} onChange={onCategoryChange} />}
      <CatalogSortBar value={sort} onChange={onSortChange} className={categories.length > 0 ? 'mt-1' : ''} />
      <div className={`${gridClassName} mt-2`}>{cases.map(renderCase)}</div>
    </div>
  );
};
